import React from 'react';
import { motion } from 'framer-motion';
import { Rocket, Users, Building2, ArrowRight, Check } from 'lucide-react';
import Section from './Section';

const audiences = [
  {
    icon: Rocket,
    title: 'SaaS Founders',
    description: 'Build thought leadership without the time drain. Turn your expertise into content that attracts and converts — automatically.',
    benefits: ['15+ hours saved weekly', 'Consistent brand presence', 'Zero content fatigue'],
    color: 'from-indigo-500 to-violet-500',
  },
  {
    icon: Users,
    title: 'Marketing Teams',
    description: 'Multiply your output without multiplying headcount. Keep messaging consistent across every campaign and channel.',
    benefits: ['10x content output', 'Brand voice lock', 'Faster go-to-market'],
    color: 'from-pink-500 to-rose-500',
  },
  {
    icon: Building2,
    title: 'Agencies',
    description: 'Scale client content delivery with consistency. Manage multiple brand voices from a single dashboard.',
    benefits: ['Multi-client ready', '3x capacity', 'White-label options'],
    color: 'from-amber-500 to-orange-500',
  },
];

const UseCases = () => {
  return (
    <Section id="use-cases" background="default" data-testid="use-cases-section">
      {/* Header */}
      <div className="text-center mb-10">
        <motion.span 
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full text-sm font-medium text-emerald-600 mb-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Users className="w-4 h-4" />
          Built For
        </motion.span>
        <motion.h2 
          className="text-3xl md:text-4xl font-bold text-slate-800 mb-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          Who Uses BrandContentFlow
        </motion.h2>
        <motion.p 
          className="text-slate-500 text-lg max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          Designed for teams that want to<br className="md:hidden" />{' '}do more with less.
        </motion.p>
      </div>

      {/* Use Case Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        {audiences.map((audience, index) => (
          <motion.div
            key={audience.title}
            className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-lg hover:border-slate-300 transition-all duration-300 group"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
          >
            {/* Icon */}
            <div className={`w-14 h-14 bg-gradient-to-br ${audience.color} rounded-2xl flex items-center justify-center mb-5 shadow-lg group-hover:scale-105 transition-transform duration-300`}>
              <audience.icon className="w-7 h-7 text-white" />
            </div>

            {/* Content */}
            <h3 className="text-xl font-bold text-slate-800 mb-2">{audience.title}</h3>
            <p className="text-slate-500 text-sm leading-relaxed mb-5">{audience.description}</p>

            {/* Benefits */}
            <div className="space-y-2">
              {audience.benefits.map((benefit) => (
                <div key={benefit} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span className="text-sm text-slate-600">{benefit}</span>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
};

export default UseCases;
